import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createNote,
  deleteNote,
  listNotes,
  updateNote,
  listProjects,
  listCompanyMembers,
  listProjectMembers,
  listNoteShares,
  createNoteShare,
  revokeNoteShare,
  dismissNoteShare,
} from "@/lib/backend";
import { demoStorageKey, isDemoModeScoped } from "@/lib/demoMode";
import type { Note, CreateNoteInput, ProjectSummary, SharePermission, CompanyMember, ProjectMember } from "@/lib/types";
import type { ListSharesResponse } from "@shared/api";
import { toast } from "@/hooks/use-toast";
import { useWorkspace } from "@/components/layout/workspace-context";
import { isPinned, loadPinnedIds, setPinned } from "@/lib/pins";
import { panelClass } from "@/components/ui/modal-panel";
import { getMiscProject, isMiscProjectId } from "@/lib/miscProject";
import {
  buildClientOptions,
  buildLocationOptions,
  buildProjectOptions,
  filterProjectsForSelection,
  getLocationLabel,
  normalizeClientKey,
  normalizeLocationKey,
  normalizeProjectSelection,
  selectionFromProjectId,
} from "@/lib/projectSelection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Filter, Pencil, Pin, PinOff, Trash2, Calendar as CalendarIcon, Paperclip } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Share2, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import NoteCard from "./components/NoteCard";

import { ModalPanel } from "@/components/ui/modal-panel";
import { CloseButton } from "@/components/ui/close-button";

const formControlClass = `
  h-9
  w-full
  rounded-md
  bg-[#141414]
  border border-white/10
  px-3
  text-sm
  text-white
  focus:outline-none
  focus:ring-2
  focus:ring-[#7b2ee6]/70
  transition
`;


function FieldLabel({ children }: { children: string }) {
  return (
    <span className="text-[15px] uppercase tracking-wide text-purple-300">
      {children}
    </span>
  );
}

const primaryCtaClass = `
  rounded-2xl
  px-5
  h-10
  bg-[#7b2ee6]
  text-white
  font-medium
  ring-1 ring-[#7b2ee6]/50
  shadow-[0_0_30px_rgba(123,46,230,0.45)]
  hover:shadow-[0_0_60px_rgba(123,46,230,0.75)]
  hover:bg-[#7b2ee6]
  active:scale-[0.98]
  transition-all
`;


type MemberOption = { user_id: string; name: string; email: string };

function formatNoteDate(created_at: string, updated_at: string): string {
  const created = new Date(created_at);
  const updated = new Date(updated_at);
  
  // If timestamps differ by more than 1 second, show "Last modified"
  if (Math.abs(updated.getTime() - created.getTime()) > 1000) {
    return `Last modified: ${updated.toLocaleDateString()} ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  return `Created: ${created.toLocaleDateString()} ${created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function parseFilterDate(input: string): Date | null {
  if (!input) return null;
  const src = input.trim();
  if (!src) return null;
  const m = src.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    let year = Number(m[3]);
    if (year < 100) {
      year += year >= 70 ? 1900 : 2000;
    }
    if (!year || !month || !day) return null;
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return null;
    return d;
  }
  const d = new Date(src);
  return isNaN(d.getTime()) ? null : d;
}

function formatFilterDateForDisplay(date: Date | null): string {
  if (!date) return "";
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  const yy = (date.getFullYear() % 100).toString().padStart(2, "0");
  return `${m}/${d}/${yy}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

type UserHeaders = { id?: string; email?: string };

interface NotesPanelProps {
  tabId?: string;
  user?: UserHeaders;
  onDirtyChange?: (dirty: boolean) => void;
  projectId?: string | null;
  projectName?: string | null;
  isMiscProject?: boolean;
}

const toolbarControlClass = `
  h-10
  rounded-2xl
  text-sm
  flex items-center
  transition-all
`;


export default function NotesPanel({ tabId, user, onDirtyChange, projectId, projectName, isMiscProject }: NotesPanelProps) {
  const { setTabScope, state } = useWorkspace();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // Inline create fields (schema-aligned with user_notes)
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newTags, setNewTags] = useState("");
  const [newClientKey, setNewClientKey] = useState<string>("");
  const [newLocationKey, setNewLocationKey] = useState<string>("");
  const [newProjectId, setNewProjectId] = useState<string>("");
  const [editing, setEditing] = useState<Note | null>(null);
  const [confirmDeleteNote, setConfirmDeleteNote] = useState<Note | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [draftTags, setDraftTags] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [formClientKey, setFormClientKey] = useState<string>("");
  const [formLocationKey, setFormLocationKey] = useState<string>("");
  const [formProjectId, setFormProjectId] = useState<string>("");
  const [formTags, setFormTags] = useState("");
  const [formIsMiscScoped, setFormIsMiscScoped] = useState(false);
  const [pinnedNoteIds, setPinnedNoteIds] = useState<Set<string>>(() => new Set());
  const [projectClientFilter, setProjectClientFilter] = useState<string>("all");
  const [projectLocationFilter, setProjectLocationFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNote, setShareNote] = useState<Note | null>(null);
  const [shareMembers, setShareMembers] = useState<Array<{ user_id: string; label: string }>>([]);
  const [shareRecipient, setShareRecipient] = useState<string>("");
  const [sharePermission, setSharePermission] = useState<SharePermission>("read");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareIncludeRevoked, setShareIncludeRevoked] = useState(false);
  const [shareList, setShareList] = useState<ListSharesResponse | null>(null);
  type NoteDateField = "created" | "updated" | null;
  const [dateFilterField, setDateFilterField] = useState<NoteDateField>(null);
  const [dateFilterStart, setDateFilterStart] = useState<string>("");
  const [dateFilterEnd, setDateFilterEnd] = useState<string>("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [gridContainerWidth, setGridContainerWidth] = useState(0);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setGridContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setGridContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const demoKey = useMemo(() => demoStorageKey("notes"), []);
  const isDemo = useMemo(
    () => isDemoModeScoped({ userId: user?.id, email: user?.email }) && Boolean(demoKey),
    [demoKey, user?.id, user?.email],
  );

  const persistDemoSnapshot = useCallback(
    (next: Note[]) => {
      if (!isDemo || !demoKey) return;
      try {
        window.localStorage.setItem(demoKey, JSON.stringify(next || []));
      } catch {
        // ignore storage failures
      }
    },
    [demoKey, isDemo],
  );

  const readDemoSnapshot = useCallback((): Note[] | null => {
    if (!isDemo || !demoKey) return null;
    try {
      const raw = window.localStorage.getItem(demoKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as Note[]) : null;
    } catch {
      return null;
    }
  }, [demoKey, isDemo]);

  const isHalfWorkspaceWidth = useMemo(() => {
    if (!tabId) return false;
    const tab = state.tabs.find((t) => t.id === tabId);
    const pos = tab?.tilePosition;
    // Treat half-width layouts as "half-workspace" horizontally.
    return (
      pos === "left-half" ||
      pos === "right-half" ||
      pos === "top-left" ||
      pos === "top-right" ||
      pos === "bottom-left" ||
      pos === "bottom-right"
    );
  }, [state.tabs, tabId]);

  const noteCardGridColsClass = useMemo(() => {
    // Important: viewport breakpoints (md/sm) are not reliable for WindowFrame sizing;
    // use tilePosition-derived "half" state instead.
    return isHalfWorkspaceWidth ? "grid-cols-[9rem_minmax(0,1fr)_18rem]" : "grid-cols-[11rem_minmax(0,1fr)_16rem]";
  }, [isHalfWorkspaceWidth]);

  const noteMetaGapClass = useMemo(() => {
    // Spend the reclaimed width on clearer separation between creator/dates and access column.
    return isHalfWorkspaceWidth ? "gap-12" : "gap-10";
  }, [isHalfWorkspaceWidth]);

  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>({});
  const loadedProjectMembersRef = useRef<Set<string>>(new Set());
  const loadedCompanyMembersRef = useRef<Set<string>>(new Set());
  const loadedSharesProfilesRef = useRef<Set<string>>(new Set());
  const loggedUnknownUserRef = useRef<Set<string>>(new Set());

  const unsavedDraft =
    Boolean(newTitle.trim()) ||
    Boolean(newContent.trim()) ||
    Boolean(newTags.trim()) ||
    (showCreateModal &&
      (draftTitle.trim().length > 0 ||
        draftContent.trim().length > 0 ||
        draftTags.trim().length > 0)) ||
    (editing !== null &&
      (draftTitle !== (editing.title || "") ||
        draftContent !== (editing.content || "") ||
        formTags.trim() !== (editing.tags ?? []).join(", ")));

  useEffect(() => {
    onDirtyChange?.(unsavedDraft);
  }, [unsavedDraft, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );

  useEffect(() => {
    // Wait for user to be defined (Clerk loaded)
    if (user === undefined) {
      console.debug("[NotesPanel] waiting for auth…");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        console.debug("[NotesPanel] loading notes…");
        const data = await listNotes(user);
        const base = Array.isArray(data) ? (data as Note[]) : [];
        console.debug("[NotesPanel] loaded", { count: base.length });
        if (isDemo) {
          const snap = readDemoSnapshot();
          if (snap) {
            setNotes(snap);
          } else {
            setNotes(base);
          }
        } else {
          setNotes(base);
        }
      } catch (e: any) {
        console.error("[NotesPanel] load error", e);
        toast({ title: "Failed to load notes", description: e?.message || "", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
  }, [user, isDemo, readDemoSnapshot]);

  function shortUserId(id?: string | null): string {
    const s = String(id || "").trim();
    if (!s) return "unknown";
    if (s.length <= 12) return s;
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
  }

  function getUserLabel(userId?: string | null): string {
    const id = String(userId || "").trim();
    if (!id) return "Unknown";
    const label = userDisplayNames[id];
    if (label) return label;
    if (user?.id && id === user.id) return "You";
    if (!loggedUnknownUserRef.current.has(id)) {
      loggedUnknownUserRef.current.add(id);
      console.debug("[NotesPanel] user display_name not resolved", { userId: id });
    }
    return "Unknown";
  }

  function getAccessLabel(n: Note, isOwner: boolean): "read" | "read/write" | "owner" {
    if (isOwner) return "owner";
    if (n.access?.kind === "shared") {
      return n.access.permission === "write" ? "read/write" : "read";
    }
    // RBAC/unscoped access: treat as editable access in UI (matches current capabilities)
    return "read/write";
  }

  function truncateToMaxChars(input: string, maxChars: number): string {
    const s = String(input || "");
    if (s.length <= maxChars) return s;
    // Use a single ellipsis character to keep within maxChars.
    return `${s.slice(0, Math.max(0, maxChars - 1))}…`;
  }

function getNoteDisplayContext(n: Note) {
  const project = projectById.get(n.project_id ?? "");

  if (!project) {
    const raw = n.project_name ?? null;
    return {
      client: null,
      project: raw?.toLowerCase() === "misc" ? "Miscellaneous" : raw,
      location: null,
    };
  }

  return {
    client: project.client_name ?? null,
    project: project.project_name ?? n.project_name ?? null,
    location: getLocationLabel(project),
  };
}


  function normalizeSingleLine(input: string): string {
    return String(input || "").replace(/\s+/g, " ").trim();
  }

  function formatCompactDateTime(iso?: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }

  function openShareDialog(n: Note) {
    if (isDemo) {
      toast({ title: "Demo mode", description: "Sharing is disabled for this demo.", variant: "default" });
      return;
    }
    setShareNote(n);
    setShareRecipient("");
    setSharePermission("read");
    setShareError(null);
    setShareIncludeRevoked(false);
    setShareList(null);
    setShareMembers([]);
    setShareOpen(true);
  }

  async function handleDismissShare(n: Note) {
    if (isDemo) {
      toast({ title: "Demo mode", description: "Sharing is disabled for this demo.", variant: "default" });
      return;
    }
    const shareId = n.access?.share_id;
    if (!shareId) return;
    try {
      await dismissNoteShare(n.id, shareId, user as any);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast({ title: "Dismissed", description: "This shared note will no longer appear in your list." });
    } catch (e: any) {
      toast({ title: "Dismiss failed", description: e?.message || "", variant: "destructive" });
    }
  }

  useEffect(() => {
    const n = shareNote;
    if (!shareOpen || !n) return;
    let cancelled = false;
    setShareLoading(true);
    setShareError(null);
    (async () => {
      try {
        // Always load company members for sharing — any company member is a valid recipient.
        let members: Array<CompanyMember | ProjectMember> = [];
        if (n.company_id) {
          members = await listCompanyMembers(n.company_id, user as any);
        } else {
          setShareError("Sharing is unavailable for this item (missing company scope).");
          members = [];
        }

        const currentUserId = user?.id || "";
        const options = (members || [])
          .filter((m: any) => (m?.user_id || "") !== currentUserId)
          .map((m: any) => {
            const profile = m?.profile || null;
            const name = (profile?.display_name || "").trim();
            const email = (profile?.email || "").trim();
            const label = name ? (email ? `${name} • ${email}` : name) : email ? email : m.user_id;
            return { user_id: m.user_id as string, label };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        const shares = await listNoteShares(n.id, { includeRevoked: shareIncludeRevoked }, user as any);

        if (cancelled) return;
        setShareMembers(options);
        setShareList(shares);
        if (!shareRecipient && options.length > 0) {
          setShareRecipient(options[0].user_id);
        }
      } catch (e: any) {
        if (!cancelled) setShareError(e?.message || "Failed to load sharing data");
      } finally {
        if (!cancelled) setShareLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareOpen, shareNote?.id, shareIncludeRevoked, user?.id]);

  async function submitShare() {
    if (!shareNote) return;
    if (!shareRecipient) return;
    try {
      await createNoteShare(shareNote.id, { principal_user_id: shareRecipient, permission: sharePermission }, user as any);
      const shares = await listNoteShares(shareNote.id, { includeRevoked: shareIncludeRevoked }, user as any);
      setShareList(shares);
      toast({
        title: "Shared",
        description: "If you need to change permission, revoke and re-share (backend is idempotent per recipient).",
      });
    } catch (e: any) {
      toast({ title: "Share failed", description: e?.message || "", variant: "destructive" });
    }
  }

  async function handleRevokeShare(shareId: string) {
    if (!shareNote) return;
    try {
      await revokeNoteShare(shareNote.id, shareId, user as any);
      const shares = await listNoteShares(shareNote.id, { includeRevoked: shareIncludeRevoked }, user as any);
      setShareList(shares);
      toast({ title: "Revoked", description: "Share revoked." });
    } catch (e: any) {
      toast({ title: "Revoke failed", description: e?.message || "", variant: "destructive" });
    }
  }

  // Load pinned notes once user is known
  useEffect(() => {
    if (user === undefined) return;
    const userId = user?.id;
    setPinnedNoteIds(loadPinnedIds(userId, "note"));
  }, [user]);

  useEffect(() => {
    if (user === undefined) {
      return;
    }
    if (!user?.id) {
      setProjects([]);
      setProjectsError(null);
      setProjectsLoading(false);
      return;
    }
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    (async () => {
      try {
        const projectData = await listProjects({ include_misc: true }, user);
        if (cancelled) return;
        setProjects(Array.isArray(projectData) ? projectData : []);
      } catch (e: any) {
        console.error("[NotesPanel] project load error", e);
        if (!cancelled) {
          setProjects([]);
          setProjectsError("Failed to load projects");
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const handler = (event: Event) => {
      const project = (event as CustomEvent<{ project?: ProjectSummary }>).detail?.project;
      if (!project?.id) return;
      setProjects((prev) => {
        const exists = prev.some((p) => p.id === project.id);
        if (exists) {
          return prev.map((p) => (p.id === project.id ? { ...p, ...project } : p));
        }
        return [...prev, project];
      });
      setProjectsError(null);
    };
    window.addEventListener("workspace:project-created", handler as EventListener);
    return () => window.removeEventListener("workspace:project-created", handler as EventListener);
  }, []);

  const projectById = useMemo(() => {
    const map = new Map<string, ProjectSummary>();
    projects.forEach((project) => {
      if (project.id) {
        map.set(project.id, project);
      }
    });
    return map;
  }, [projects]);
  const miscProject = useMemo(() => getMiscProject(projects), [projects]);
  const miscProjectId = miscProject?.id ?? null;

  const canUseProjectForNotes = useCallback((proj: ProjectSummary): boolean => {
    if (!proj?.id) return false;
    const name = (proj.project_name || "").trim().toLowerCase();
    return name !== "misc";
  }, []);
  const canUseProjectForForm = useCallback(
    (proj: ProjectSummary): boolean => canUseProjectForNotes(proj) || proj.id === formProjectId,
    [canUseProjectForNotes, formProjectId],
  );

  const newClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForNotes }),
    [projects, canUseProjectForNotes],
  );
  const newLocationOptions = useMemo(
    () => buildLocationOptions(projects, newClientKey, { filter: canUseProjectForNotes }),
    [projects, newClientKey, canUseProjectForNotes],
  );
  const newProjectsForLocation = useMemo(
    () =>
      filterProjectsForSelection(
        projects,
        { clientKey: newClientKey, locationKey: newLocationKey },
        { filter: canUseProjectForNotes },
      ),
    [projects, newClientKey, newLocationKey, canUseProjectForNotes],
  );
  const newProjectOptions = useMemo(
    () => buildProjectOptions(newProjectsForLocation),
    [newProjectsForLocation],
  );

  const formClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForForm }),
    [projects, canUseProjectForForm],
  );
  const formLocationOptions = useMemo(
    () => buildLocationOptions(projects, formClientKey, { filter: canUseProjectForForm }),
    [projects, formClientKey, canUseProjectForForm],
  );
  const formProjectsForLocation = useMemo(
    () =>
      filterProjectsForSelection(
        projects,
        { clientKey: formClientKey, locationKey: formLocationKey },
        { filter: canUseProjectForForm },
      ),
    [projects, formClientKey, formLocationKey, canUseProjectForForm],
  );
  const formProjectOptions = useMemo(
    () => buildProjectOptions(formProjectsForLocation),
    [formProjectsForLocation],
  );

  const filterClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForNotes }),
    [projects, canUseProjectForNotes],
  );
  const filterLocationOptions = useMemo(() => {
    const base = projects.filter(canUseProjectForNotes);
    if (projectClientFilter === "all") {
      const map = new Map<string, { key: string; label: string }>();
      base.forEach((project) => {
        const key = normalizeLocationKey(project);
        if (!map.has(key)) {
          map.set(key, { key, label: getLocationLabel(project) });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    return buildLocationOptions(base, projectClientFilter);
  }, [projects, projectClientFilter, canUseProjectForNotes]);
  const filterProjectsForOptions = useMemo(() => {
    const base = projects.filter(canUseProjectForNotes);
    if (projectClientFilter === "all" && projectLocationFilter === "all") {
      return base;
    }
    if (projectClientFilter === "all" && projectLocationFilter !== "all") {
      return base.filter((project) => normalizeLocationKey(project) === projectLocationFilter);
    }
    if (projectClientFilter !== "all" && projectLocationFilter === "all") {
      return base.filter((project) => normalizeClientKey(project) === projectClientFilter);
    }
    return filterProjectsForSelection(
      base,
      { clientKey: projectClientFilter, locationKey: projectLocationFilter },
    );
  }, [projects, projectClientFilter, projectLocationFilter, canUseProjectForNotes]);
  const filterProjectOptions = useMemo(
    () => buildProjectOptions(filterProjectsForOptions),
    [filterProjectsForOptions],
  );

  useEffect(() => {
    const normalized = normalizeProjectSelection(
      projects,
      { clientKey: newClientKey, locationKey: newLocationKey, projectId: newProjectId },
      { filter: canUseProjectForNotes, preserveEmptyProject: true },
    );
    if (normalized.clientKey !== newClientKey) setNewClientKey(normalized.clientKey);
    if (normalized.locationKey !== newLocationKey) setNewLocationKey(normalized.locationKey);
    if (normalized.projectId !== newProjectId) setNewProjectId(normalized.projectId);
  }, [projects, newClientKey, newLocationKey, newProjectId, canUseProjectForNotes]);

  useEffect(() => {
    const normalized = normalizeProjectSelection(
      projects,
      { clientKey: formClientKey, locationKey: formLocationKey, projectId: formProjectId },
      { filter: canUseProjectForForm, preserveEmptyProject: true },
    );
    if (normalized.clientKey !== formClientKey) setFormClientKey(normalized.clientKey);
    if (normalized.locationKey !== formLocationKey) setFormLocationKey(normalized.locationKey);
    if (normalized.projectId !== formProjectId) setFormProjectId(normalized.projectId);
  }, [projects, formClientKey, formLocationKey, formProjectId, canUseProjectForForm]);

  useEffect(() => {
    if (projectFilter === "all" || projectFilter === "__misc__") return;
    const selection = selectionFromProjectId(projects, projectFilter);
    if (!selection) return;
    if (projectClientFilter !== selection.clientKey) setProjectClientFilter(selection.clientKey);
    if (projectLocationFilter !== selection.locationKey) setProjectLocationFilter(selection.locationKey);
  }, [projectFilter, projects, projectClientFilter, projectLocationFilter]);

  useEffect(() => {
    if (projectLocationFilter === "all") return;
    const locationKeys = filterLocationOptions.map((loc) => loc.key);
    if (!locationKeys.includes(projectLocationFilter)) {
      setProjectLocationFilter("all");
    }
  }, [filterLocationOptions, projectLocationFilter]);

  useEffect(() => {
    if (projectFilter === "all" || projectFilter === "__misc__") return;
    if (projects.length === 0) return;
    const allowed = new Set(filterProjectOptions.map((opt) => opt.value));
    if (!allowed.has(projectFilter)) {
      setProjectFilter("all");
    }
  }, [projectFilter, filterProjectOptions, projects.length]);


  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    (Array.isArray(notes) ? notes : []).forEach((n) => {
      if (Array.isArray(n.tags)) {
        n.tags.forEach((tag) => {
          if (tag) set.add(tag);
        });
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [notes]);

  const scopeInfo = useMemo(() => {
    if (isMiscProject) {
      return {
        label: "Window: Misc bucket",
        description: "Window is scoped to notes in the Misc project.",
      };
    }
    if (projectId) {
      const meta = projectById.get(projectId);
      const name = meta?.project_name || projectName || "Project";
      const client = meta?.client_name;
      return {
        label: client ? `Project: ${name} • ${client}` : `Project: ${name}`,
        description: "Window is scoped to this project.",
      };
    }
    return {
      label: "Window: All notes",
      description: "Use Filters to narrow by project; ALL shows project and non-project notes.",
    };
  }, [projectById, projectId, projectName, isMiscProject]);

  // Default the inline "Add a New Note" project dropdown from the window scope - responds to all scope changes
  useEffect(() => {
    if (isMiscProject && isMiscProjectId(projectId, miscProjectId)) {
      setNewProjectId("");
    } else if (projectId) {
      const selection = selectionFromProjectId(projects, projectId);
      if (selection) {
        setNewClientKey(selection.clientKey);
        setNewLocationKey(selection.locationKey);
        setNewProjectId(selection.projectId);
      }
    } else {
      // Unscoped - reset to empty
      setNewProjectId("");
    }
  }, [projectId, isMiscProject, projects, miscProjectId]);

  // Default the create modal project dropdown from the window scope - responds to all scope changes
  useEffect(() => {
    if (isMiscProject && isMiscProjectId(projectId, miscProjectId)) {
      setFormProjectId("");
    } else if (projectId) {
      const selection = selectionFromProjectId(projects, projectId);
      if (selection) {
        setFormClientKey(selection.clientKey);
        setFormLocationKey(selection.locationKey);
        setFormProjectId(selection.projectId);
      }
    } else {
      // Unscoped - reset to empty
      setFormProjectId("");
    }
  }, [projectId, isMiscProject, projects, miscProjectId]);

  // Handle all scope transitions: initial mount, project changes, misc changes, and unscoped changes
  const prevScopeRef = useRef<{ projectId: string | null; isMisc: boolean } | null>(null);
  // Synchronous flag: when true, the setTabScope effect must skip because
  // we just pushed a filter update from incoming props (not from user action).
  const skipSyncBackRef = useRef(false);
  
  useEffect(() => {
    const prev = prevScopeRef.current;
    const current = { projectId: projectId ?? null, isMisc: Boolean(isMiscProject) };
    
    // Detect ANY scope change (not just to unscoped)
    const scopeChanged = prev && (
      prev.projectId !== current.projectId || 
      prev.isMisc !== current.isMisc
    );
    
    if (scopeChanged) {
      // Tell the setTabScope effect to skip — this filter change comes from props, not user action
      skipSyncBackRef.current = true;

      // Update filter to match new scope (isMisc takes priority — sidebar
      // passes both projectId and isMisc for the Misc bucket)
      if (current.isMisc) {
        setProjectFilter("__misc__");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      } else if (current.projectId) {
        const selection = selectionFromProjectId(projects, current.projectId);
        if (selection) {
          setProjectClientFilter(selection.clientKey);
          setProjectLocationFilter(selection.locationKey);
        }
        setProjectFilter(current.projectId);
      } else {
        // Transitioning to unscoped "all" view
        setProjectFilter("all");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      }
      
      // Clear other filters on any scope change
      setTagFilter("all");
      setDateFilterField(null);
      setDateFilterStart("");
      setDateFilterEnd("");
    } else if (!prev) {
      // Initial mount - set filter based on initial scope
      // Also skip sync-back for initial mount
      skipSyncBackRef.current = true;

      if (current.isMisc) {
        setProjectFilter("__misc__");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      } else if (current.projectId) {
        const selection = selectionFromProjectId(projects, current.projectId);
        if (selection) {
          setProjectClientFilter(selection.clientKey);
          setProjectLocationFilter(selection.locationKey);
        }
        setProjectFilter(current.projectId);
      }
      // else: leave at default "all"
    }
    
    prevScopeRef.current = current;
  }, [projectId, isMiscProject, projects]);


  useEffect(() => {
    if (!tabId) return;

    // If prevScopeRef just pushed a filter update from props, skip this run
    if (skipSyncBackRef.current) {
      skipSyncBackRef.current = false;
      return;
    }
    
    // Determine what the filter SHOULD be based on current props
    // isMiscProject takes priority (sidebar passes both projectId and isMisc)
    const expectedFilter = isMiscProject
      ? "__misc__"
      : projectId
        ? projectId
        : "all";
    
    // Only call setTabScope if user changed the filter to something different than props
    if (projectFilter === expectedFilter) {
      return; // Filter matches props, no need to sync back
    }
    
    // User manually changed the filter, sync it back to Workspace
    if (projectFilter === "__misc__") {
      setTabScope(tabId, {
        projectId: miscProjectId,
        projectName: miscProject?.project_name ?? "Misc",
        clientName: miscProject?.client_name ?? null,
        isMiscProject: true,
      });
      return;
    }
    if (projectFilter === "all") {
      setTabScope(tabId, { projectId: null, projectName: null, clientName: null, isMiscProject: false });
      return;
    }
    const meta = projectById.get(projectFilter);
    setTabScope(tabId, {
      projectId: projectFilter,
      projectName: meta?.project_name ?? projectName ?? null,
      clientName: meta?.client_name ?? null,
      isMiscProject: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, projectFilter, projectById, setTabScope, miscProjectId, miscProject]);

  function matchesNoteDateFilter(n: Note): boolean {
    if (!dateFilterField && !dateFilterStart && !dateFilterEnd) return true;
    const src = dateFilterField === "created" ? n.created_at : n.updated_at;
    if (!src) return false;
    const d = new Date(src);
    if (isNaN(d.getTime())) return false;
    const ts = d.getTime();
    const startDate = dateFilterStart ? startOfDay(parseFilterDate(dateFilterStart) || new Date(NaN)) : null;
    const endDate = dateFilterEnd ? endOfDay(parseFilterDate(dateFilterEnd) || new Date(NaN)) : null;
    if (startDate && isNaN(startDate.getTime())) return false;
    if (endDate && isNaN(endDate.getTime())) return false;
    if (startDate && ts < startDate.getTime()) return false;
    if (endDate && ts > endDate.getTime()) return false;
    return true;
  }

  function clearAllFilters() {
    if (!projectId && !isMiscProject) {
      setProjectFilter("all");
      setProjectClientFilter("all");
      setProjectLocationFilter("all");
    }
    setTagFilter("all");
    setDateFilterField(null);
    setDateFilterStart("");
    setDateFilterEnd("");
  }

  const hasActiveFilters =
    projectFilter !== "all" ||
    projectClientFilter !== "all" ||
    projectLocationFilter !== "all" ||
    tagFilter !== "all" ||
    !!dateFilterField ||
    !!dateFilterStart ||
    !!dateFilterEnd;

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    let base = Array.isArray(notes) ? notes : [];
    // Apply project filter first (regardless of origin scope)
    if (projectFilter === "__misc__") {
      base = miscProjectId ? base.filter((n) => (n.project_id ?? null) === miscProjectId) : [];
    } else if (projectFilter !== "all") {
      base = base.filter((n) => (n.project_id ?? null) === projectFilter);
    } else if (projectClientFilter !== "all" || projectLocationFilter !== "all") {
      base = base.filter((n) => {
        if (!n.project_id) return false;
        const meta = projectById.get(n.project_id);
        if (!meta) return false;
        if (projectClientFilter !== "all" && normalizeClientKey(meta) !== projectClientFilter) return false;
        if (projectLocationFilter !== "all" && normalizeLocationKey(meta) !== projectLocationFilter) return false;
        return true;
      });
    }
    if (tagFilter !== "all") {
      base = base.filter((n) => {
        const tags = Array.isArray(n.tags) ? n.tags : [];
        return tags.includes(tagFilter);
      });
    }
    base = base.filter((n) => matchesNoteDateFilter(n));
    const filtered = !term
      ? base
      : base.filter((n) => {
          const title = (n.title || "").toLowerCase();
          const content = (n.content || "").toLowerCase();
          const tagsText = Array.isArray(n.tags) ? n.tags.join(" ").toLowerCase() : "";
          const proj = (n.project_name || "").toLowerCase();
          return (
            title.includes(term) ||
            content.includes(term) ||
            tagsText.includes(term) ||
            proj.includes(term)
          );
        });
    const sorted = filtered.slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    const pinned = sorted.filter((n) => isPinned(pinnedNoteIds, n.id));
    const unpinned = sorted.filter((n) => !isPinned(pinnedNoteIds, n.id));
    return [...pinned, ...unpinned];
  }, [
    q,
    notes,
    projectId,
    projectName,
    isMiscProject,
    pinnedNoteIds,
    projectById,
    projectFilter,
    projectClientFilter,
    projectLocationFilter,
    tagFilter,
    dateFilterField,
    dateFilterStart,
    dateFilterEnd,
    miscProjectId,
  ]);

  // Best-effort display_name resolution for creator/sharer without leaking user ids in UI.
  useEffect(() => {
    if (!user?.id) return;
    const neededUserIds = new Set<string>();
    const projectIds = new Set<string>();
    const companyIds = new Set<string>();

    (visible || []).forEach((n) => {
      const ownerId = String(n.user_id || "").trim();
      if (ownerId) neededUserIds.add(ownerId);
      const sharedBy = String(n.access?.shared_by || "").trim();
      if (sharedBy) neededUserIds.add(sharedBy);
      if (n.project_id) projectIds.add(n.project_id);
      else if (n.company_id) companyIds.add(n.company_id);
    });

    const missingProjects = Array.from(projectIds).filter((id) => !loadedProjectMembersRef.current.has(id));
    const missingCompanies = Array.from(companyIds).filter((id) => !loadedCompanyMembersRef.current.has(id));
    if (missingProjects.length === 0 && missingCompanies.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all([
          ...missingProjects.map(async (projectId) => {
            const members = await listProjectMembers(projectId, user as any);
            return { kind: "project" as const, id: projectId, members };
          }),
          ...missingCompanies.map(async (companyId) => {
            const members = await listCompanyMembers(companyId, user as any);
            return { kind: "company" as const, id: companyId, members };
          }),
        ]);
        if (cancelled) return;

        const next: Record<string, string> = {};
        for (const r of results) {
          if (r.kind === "project") loadedProjectMembersRef.current.add(r.id);
          if (r.kind === "company") loadedCompanyMembersRef.current.add(r.id);
          for (const m of (r as any).members || []) {
            const userId = String(m?.user_id || "").trim();
            if (!userId) continue;
            const profile = m?.profile || null;
            const display = String(profile?.display_name || "").trim();
            const email = String(profile?.email || "").trim();
            const label = display || email;
            if (label) next[userId] = label;
          }
        }

        // Only keep names we might actually need (avoid unbounded growth)
        const filtered: Record<string, string> = {};
        Object.entries(next).forEach(([id, name]) => {
          if (neededUserIds.has(id)) filtered[id] = name;
        });
        if (Object.keys(filtered).length) {
          setUserDisplayNames((prev) => ({ ...prev, ...filtered }));
        }
      } catch (e: any) {
        console.debug("[NotesPanel] failed to resolve user display names", e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, user?.id]);

  // For shared notes, fall back to GET /notes/:id/shares to resolve display_name via profiles[].
  useEffect(() => {
    if (!user?.id) return;
    const shared = (visible || []).filter((n) => n.access?.kind === "shared");
    if (shared.length === 0) return;

    const missingNoteIds = shared
      .filter((n) => !loadedSharesProfilesRef.current.has(n.id))
      .filter((n) => {
        const sharedBy = String(n.access?.shared_by || "").trim();
        if (!sharedBy || sharedBy === user.id) return false;
        return !userDisplayNames[sharedBy];
      })
      .map((n) => n.id);
    if (missingNoteIds.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const noteId of missingNoteIds) {
        if (cancelled) return;
        loadedSharesProfilesRef.current.add(noteId);
        try {
          const data = await listNoteShares(noteId, { includeRevoked: false }, user as any);
          if (cancelled) return;
          const next: Record<string, string> = {};
          (data?.profiles || []).forEach((p) => {
            const id = String(p?.user_id || "").trim();
            if (!id) return;
            const label = String(p?.display_name || "").trim() || String(p?.email || "").trim();
            if (label) next[id] = label;
          });
          if (Object.keys(next).length) setUserDisplayNames((prev) => ({ ...prev, ...next }));
        } catch (e: any) {
          // Best-effort: receivers may not be allowed to list shares; ignore.
          console.debug("[NotesPanel] failed to resolve sharer via /shares", { noteId, err: e?.message || e });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, user?.id, userDisplayNames]);

  function showAttachmentsComingSoonToast() {
    toast({
      title: "Attachments coming soon",
      description: "Image/file attachments are in active development.",
    });
  }

  function buildMemberOptionsForUi(members: Array<CompanyMember | ProjectMember>): MemberOption[] {
    const currentUserId = user?.id || "";
    return (members || [])
      .filter((m: any) => (m?.user_id || "") !== currentUserId)
      .map((m: any) => {
        const profile = m?.profile || null;
        const displayName = String(profile?.display_name || "").trim();
        const email = String(profile?.email || "").trim();
        // Critical: never show ids in UI.
        const safeName = displayName || (email ? email : "Unknown");
        return { user_id: String(m.user_id || ""), name: safeName, email: email || "—" };
      })
      .filter((x) => x.user_id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function parseTags(input: string): string[] | null {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const parts = trimmed
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    return parts.length ? parts : null;
  }

  function openCreateModal() {
    setDraftTitle("");
    setDraftContent("");
    setDraftTags("");
    setIsExpanded(false);
    setFormIsMiscScoped(!!isMiscProject);
    setShowCreateModal(true);
  }

  async function handleCreateNote() {
    const title = draftTitle.trim();
    const content = draftContent.trim();
    if (!content) return;

    const tags = parseTags(draftTags);
    const input: CreateNoteInput = {
      title: title || content.slice(0, 40),
      content,
      tags,
    };

    if (formIsMiscScoped) {
      input.project_id = miscProjectId;
      input.project_name = miscProject?.project_name ?? "Misc";
    } else if (formProjectId) {
      const p = projectById.get(formProjectId);
      if (p?.id) {
        input.project_id = p.id;
        input.project_name = p.project_name ?? null;
      }
    } else if (projectId) {
      input.project_id = projectId;
      input.project_name = projectName ?? null;
    } else if (isMiscProject) {
      input.project_id = miscProjectId;
      input.project_name = miscProject?.project_name ?? "Misc";
    }
    
    const temp: Note = {
      id: `temp:${Math.random().toString(36).slice(2)}`,
      user_id: "me",
      title: input.title || "Untitled",
      content,
      tags: input.tags ?? null,
      project_id: (input as any).project_id ?? null,
      project_name:
        input.project_id != null
          ? input.project_name ?? null
          : isMiscProject
          ? "Misc"
          : null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as Note;
    
    setNotes((prev) => {
      const next = [temp, ...prev];
      persistDemoSnapshot(next);
      return next;
    });
    setShowCreateModal(false);
    setIsExpanded(false);
    setDraftTags("");
    
    if (isDemo) {
      toast({ title: "Saved locally", description: "This note will not be sent to the backend in demo mode." });
      return;
    }

    try {
      console.debug("[NotesPanel] create", { inputPreview: input.content.slice(0, 80) });
      const saved = await createNote(input, user);
      console.debug("[NotesPanel] create ok", { id: saved.id });
      setNotes((prev) => {
        const next = prev.map((n) => (n.id === temp.id ? saved : n));
        persistDemoSnapshot(next);
        return next;
      });
    } catch (e: any) {
      console.error("[NotesPanel] create error", e);
      setNotes((prev) => prev.filter((n) => n.id !== temp.id));
      toast({ title: "Create failed", description: e?.message || "", variant: "destructive" });
    }
  }

  function handleDelete(n: Note) {
    const isOwner = !!user?.id && n.user_id === user.id;
    if (!isOwner && n.access?.share_id) {
      return handleDismissShare(n);
    }
    setConfirmDeleteNote(n);
  }

  async function executeDeleteNote(n: Note) {
    const prev = notes;
    setNotes((arr) => {
      const next = arr.filter((x) => x.id !== n.id);
      persistDemoSnapshot(next);
      return next;
    });
    try {
      console.debug("[NotesPanel] delete", { id: n.id });
      if (isDemo) return;
      await deleteNote(n.id, user);
    } catch (e: any) {
      console.error("[NotesPanel] delete error", e);
      setNotes(prev);
      toast({ title: "Delete failed", description: e?.message || "", variant: "destructive" });
    }
  }

  function openEditor(n: Note) {
    setEditing(n);
    setDraftTitle(n.title || "");
    setDraftContent(n.content || "");
    setIsExpanded(false);
    if (n.project_id) {
      const selection = selectionFromProjectId(projects, n.project_id);
      if (selection) {
        setFormClientKey(selection.clientKey);
        setFormLocationKey(selection.locationKey);
      }
    } else {
      setFormClientKey("");
      setFormLocationKey("");
    }
    setFormProjectId(n.project_id ?? "");
    setFormTags((n.tags ?? []).join(", "));
    setFormIsMiscScoped(isMiscProjectId(n.project_id ?? null, miscProjectId));
  }

  async function saveEditor() {
    const n = editing;
    if (!n) return;
    const prev = notes;
    const changes: Partial<Note> = {};
    if (draftTitle !== (n.title || "")) {
      changes.title = draftTitle;
    }
    if (draftContent !== (n.content || "")) {
      changes.content = draftContent;
    }
    const nextTags = parseTags(formTags);
    const prevTags = n.tags ?? null;
    if (JSON.stringify(prevTags) !== JSON.stringify(nextTags)) {
      (changes as any).tags = nextTags;
    }
    const selectedProject = formIsMiscScoped ? miscProject ?? undefined : (formProjectId ? projectById.get(formProjectId) : undefined);
    const desiredProjectId = formIsMiscScoped ? (miscProjectId ?? null) : (formProjectId ? formProjectId : null);
    let desiredProjectName: string | null | undefined;
    if (formIsMiscScoped) {
      desiredProjectName = miscProject?.project_name ?? "Misc";
    } else if (desiredProjectId) {
      desiredProjectName = selectedProject?.project_name ?? n.project_name ?? null;
    } else {
      desiredProjectName = undefined;
    }
    if ((n.project_id ?? null) !== desiredProjectId) {
      (changes as any).project_id = desiredProjectId;
    }
    if (desiredProjectName !== undefined && (n.project_name ?? null) !== desiredProjectName) {
      (changes as any).project_name = desiredProjectName;
    }

    if (Object.keys(changes).length === 0) {
      setEditing(null);
      return;
    }

    const updated: Note = {
      ...n,
      ...changes,
      ...(changes.project_id !== undefined ? { project_id: (changes as any).project_id ?? null } : {}),
      ...(changes.project_name !== undefined ? { project_name: (changes as any).project_name ?? null } : {}),
      ...(changes.tags !== undefined ? { tags: (changes as any).tags ?? null } : {}),
      updated_at: new Date().toISOString(),
    } as Note;
    setNotes((arr) => {
      const next = arr.map((x) => (x.id === n.id ? updated : x));
      persistDemoSnapshot(next);
      return next;
    });
    try {
      console.debug("[NotesPanel] update", { id: n.id, title: draftTitle, contentPreview: draftContent.slice(0, 80) });
      if (isDemo) {
        setEditing(null);
        return;
      }
      await updateNote(
        n.id,
        {
          title: changes.title,
          content: changes.content,
          project_id: (changes as any).project_id,
          project_name: (changes as any).project_name,
        },
        user,
      );
      setEditing(null);
    } catch (e: any) {
      console.error("[NotesPanel] update error", e);
      setNotes(prev);
      toast({ title: "Update failed", description: e?.message || "", variant: "destructive" });
    }
  }

  function handleTogglePin(id: string) {
    const userId = user?.id;
    setPinnedNoteIds((prev) => setPinned(userId, "note", id, !prev.has(id)));
  }

  return (

  <div className="space-y-1 px-3 py-2">

    {/* Notes Toolbar */}
    <div className="relative z-10 flex items-center gap-3 pb-1 w-full min-w-0 mt-2 mb-4">

    {/* CREATE NOTE Button*/}
    <button
      type="button"
      onClick={openCreateModal}
      className={`
        px-4 py-1.5
        text-[14px] text-white
        rounded-2xl
        transition-all

        bg-purple-700/40
        ring-1 ring-[#7b2ee6]/60

        hover:bg-purple-500/40
        hover:ring-[#7b2ee6]/60
      `}
    >
      Create Note
    </button>

    {/* CREATE NOTE Modal*/}
    <Dialog open={showCreateModal} onOpenChange={(o) => !o && setShowCreateModal(false)}>
      <DialogContent className="w-full max-w-[500px] bg-transparent border-none p-0 [&>button]:hidden">
        <ModalPanel>

          <DialogHeader>
            <DialogTitle>Create Note</DialogTitle>
            <DialogDescription>
              Capture meeting notes and decisions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">

            {/* Title */}
            <div className="flex flex-col gap-1">
              <FieldLabel>Title</FieldLabel>
              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                className={formControlClass}
              />
            </div>

            {/* Content */}
            <div className="flex flex-col gap-1">
              <FieldLabel>Content</FieldLabel>
              <textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                className={`${formControlClass} min-h-[120px]`}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formIsMiscScoped}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormIsMiscScoped(checked);
                  if (checked) {
                    setFormClientKey("");
                    setFormLocationKey("");
                    setFormProjectId("");
                  }
                }}
                className="accent-[#7b2ee6] h-4 w-4"
              />
              <span className="text-sm text-muted-foreground">Miscellaneous</span>
            </label>

            {/* Project grid (same as task modal layout) */}
            <div className="grid grid-cols-2 gap-x-10 gap-y-4">

              <div className={`flex flex-col gap-3 transition-opacity ${formIsMiscScoped ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="flex flex-col gap-1">
                  <FieldLabel>Client</FieldLabel>
                  <select
                    value={formClientKey}
                    disabled={formIsMiscScoped}
                    onChange={(e) => {
                      setFormClientKey(e.target.value);
                      setFormLocationKey("");
                      setFormProjectId("");
                    }}
                    className={formControlClass}
                  >
                    <option value="">—</option>
                    {formClientOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <FieldLabel>Location</FieldLabel>
                  <select
                    value={formLocationKey}
                    disabled={formIsMiscScoped}
                    onChange={(e) => {
                      setFormLocationKey(e.target.value);
                      setFormProjectId("");
                    }}
                    className={formControlClass}
                  >
                    <option value="">—</option>
                    {formLocationOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <FieldLabel>Project</FieldLabel>
                  <select
                    value={formProjectId}
                    disabled={formIsMiscScoped}
                    onChange={(e) => setFormProjectId(e.target.value)}
                    className={formControlClass}
                  >
                    <option value="">Misc</option>
                    {formProjectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <FieldLabel>Tags</FieldLabel>
                  <input
                    value={draftTags}
                    onChange={(e) => setDraftTags(e.target.value)}
                    placeholder="comma separated"
                    className={formControlClass}
                  />
                </div>
              </div>

            </div>

          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">

            <Button
              variant="secondary"
              className="rounded-2xl px-4 bg-black/40 ring-1 ring-white/10 hover:bg-white/10 transition"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>

            <Button
              className={primaryCtaClass}
              onClick={handleCreateNote}
              disabled={!draftContent.trim()}
            >
              Create
            </Button>


          </DialogFooter>

        </ModalPanel>
      </DialogContent>
    </Dialog>

      {/* Search */}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search..."
        className="
          flex-1
          text-[14px]
          px-4 py-1.5
          rounded-2xl
          bg-black/40
          ring-1 ring-[#7b2ee6]/40
          text-white
          focus:outline-none
          focus:ring-[#7b2ee6]/70
          transition-all
        "
      />

      {/* Note Filter Button */}
      <button
        onClick={() => setFiltersOpen(true)}
        className={`
          px-4 py-1.5
          text-[14px] font-medium
          flex items-center gap-2
          rounded-3xl
          transition-all

          ${hasActiveFilters
            ? `
              bg-purple-700/20
              ring-2 ring-[#7b2ee6]
              text-white
            `
            : `
              ${panelClass}
              text-white/80
              hover:bg-purple-700/10
              hover:ring-[#7b2ee6]/60
            `}
        `}
      >
        <Filter className="h-3 w-3" />
        <span>Filters</span>
      </button>
    </div>

    {/* Notes Grid */}
    {loading ? (
      <div className="text-xs text-muted-foreground">Loading…</div>
    ) : (
      <div
        ref={gridContainerRef}
        className="grid gap-6"
        style={{
          gridTemplateColumns: `repeat(${Math.max(1, Math.floor((gridContainerWidth || 600) / 280))}, minmax(0, 1fr))`,
        }}
      >
        {visible.map((n) => (
          <NoteCard
            key={n.id}
            note={n}
            isExpanded={expandedNoteId === n.id}
            onToggleExpand={() =>
              setExpandedNoteId((prev) => (prev === n.id ? null : n.id))
            }
            onTogglePin={() => handleTogglePin(n.id)}
            onEdit={() => openEditor(n)}
            onDelete={() => handleDelete(n)}
            onShare={() => openShareDialog(n)}
            onDismissShare={() => handleDismissShare(n)}
            isPinned={isPinned(pinnedNoteIds, n.id)}
            creatorLabel={getUserLabel(n.user_id)}
            sharerLabel={getUserLabel(n.access?.shared_by)}
            accessLabel={getAccessLabel(n, !!user?.id && n.user_id === user.id)}
            isOwner={!!user?.id && n.user_id === user.id}
            projectMeta={getNoteDisplayContext(n)}
            isHalfWorkspaceWidth={isHalfWorkspaceWidth}
            noteCardGridColsClass={noteCardGridColsClass}
            noteMetaGapClass={noteMetaGapClass}
          />
        ))}
      </div>
    )}

    {/* Note Filter Modal*/}
    <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
      <DialogContent
        className="
          fixed
          left-1/2
          top-1/2
          -translate-x-1/2
          -translate-y-1/2
          p-0
          border-0
          bg-transparent
          shadow-none
          max-w-[620px]
          w-[min(620px,92vw)]
          [&>button]:hidden
        "
      >

        <VisuallyHidden>
          <DialogTitle>Filter Notes</DialogTitle>
          <DialogDescription>Filter notes by project, tags, and date</DialogDescription>
        </VisuallyHidden>


        {/*Filtering Modal*/}
        <ModalPanel className="p-4 min-w-[300px]">

          {/* PROJECT */}
          <div className="space-y-1">
            <div className="text-[11px] tracking-wide text-white/60 font-semibold uppercase">Project</div>

            <select
              value={projectClientFilter}
              onChange={(e) => {
                setProjectClientFilter(e.target.value);
                setProjectLocationFilter("all");
                setProjectFilter("all");
              }}
              className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
            >
              <option value="all">All clients</option>
              {filterClientOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            <select
              value={projectLocationFilter}
              onChange={(e) => {
                setProjectLocationFilter(e.target.value);
                setProjectFilter("all");
              }}
              className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
            >
              <option value="all">All locations</option>
              {filterLocationOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
            >
              <option value="all">All projects</option>
              <option value="__misc__">Misc</option>
              {filterProjectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="border-t border-white/10 my-2" />

          {/* TAGS */}
          <div className="space-y-1">
            <div className="text-[11px] tracking-wide text-white/60 font-semibold uppercase">Tags</div>

            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
            >
              <option value="all">All tags</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div className="border-t border-white/10 my-2" />

          {/* DATE */}
          <div className="space-y-1">
            <div className="text-[11px] tracking-wide text-white/60 font-semibold uppercase">Date</div>

            <select
              value={dateFilterField || ""}
              onChange={(e) =>
                setDateFilterField(e.target.value === "" ? null : (e.target.value as any))
              }
              className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
            >
              <option value="">No date filter</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
            </select>

            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="MM/DD/YY"
                value={dateFilterStart}
                onChange={(e) => setDateFilterStart(e.target.value)}
                className="h-9 text-sm border rounded-md px-2 bg-[#0b0b0c]"
              />

              <input
                type="text"
                placeholder="MM/DD/YY"
                value={dateFilterEnd}
                onChange={(e) => setDateFilterEnd(e.target.value)}
                className="h-9 text-sm border rounded-md px-2 bg-[#0b0b0c]"
              />
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              onClick={clearAllFilters}
              className="text-[11px] px-2 py-1 rounded border border-border bg-window hover:bg-window/80"
            >
              Clear filters
            </button>
          </div>

        </ModalPanel>

      </DialogContent>
    </Dialog>

    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent
        className="
          w-full
          max-w-[500px]
          bg-transparent
          border-none
          p-0
          [&>button]:hidden
        "
      >
        <ModalPanel>

          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>
              Update note details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">

            {/* Title */}
            <div className="flex flex-col gap-1">
              <FieldLabel>Title</FieldLabel>
              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                className={formControlClass}
              />
            </div>

            {/* Content */}
            <div className="flex flex-col gap-1">
              <FieldLabel>Content</FieldLabel>
              <textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                className={`${formControlClass} min-h-[120px]`}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formIsMiscScoped}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormIsMiscScoped(checked);
                  if (checked) {
                    setFormClientKey("");
                    setFormLocationKey("");
                    setFormProjectId("");
                  }
                }}
                className="accent-[#7b2ee6] h-4 w-4"
              />
              <span className="text-sm text-muted-foreground">Miscellaneous</span>
            </label>

            {/* SAME GRID AS TASK MODAL */}
            <div className="grid grid-cols-2 gap-x-10 gap-y-4">

              {/* LEFT */}
              <div className={`flex flex-col gap-3 transition-opacity ${formIsMiscScoped ? "opacity-50 pointer-events-none" : ""}`}>

                <div className="flex flex-col gap-1">
                  <FieldLabel>Client</FieldLabel>
                  <select
                    value={formClientKey}
                    disabled={formIsMiscScoped}
                    onChange={(e) => {
                      setFormClientKey(e.target.value);
                      setFormLocationKey("");
                      setFormProjectId("");
                    }}
                    className={formControlClass}
                  >
                    <option value="">—</option>
                    {formClientOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <FieldLabel>Location</FieldLabel>
                  <select
                    value={formLocationKey}
                    disabled={formIsMiscScoped}
                    onChange={(e) => {
                      setFormLocationKey(e.target.value);
                      setFormProjectId("");
                    }}
                    className={formControlClass}
                  >
                    <option value="">—</option>
                    {formLocationOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <FieldLabel>Project</FieldLabel>
                  <select
                    value={formProjectId}
                    disabled={formIsMiscScoped}
                    onChange={(e) => setFormProjectId(e.target.value)}
                    className={formControlClass}
                  >
                    <option value="">Misc</option>
                    {formProjectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

              </div>

              {/* RIGHT */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <FieldLabel>Tags</FieldLabel>
                  <input
                    value={formTags}
                    onChange={(e) => setFormTags(e.target.value)}
                    placeholder="comma separated"
                    className={formControlClass}
                  />
                </div>
              </div>

            </div>

          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">

            <Button
              variant="secondary"
              className="rounded-2xl px-4 bg-black/40 ring-1 ring-white/10 hover:bg-white/10 transition"
              onClick={() => setEditing(null)}
            >
              Cancel
            </Button>

            <Button
              className={primaryCtaClass}
              onClick={saveEditor}
            >
              Save
            </Button>

          </DialogFooter>

        </ModalPanel>
      </DialogContent>
    </Dialog>


    {/* Note Share Modal */}
    <Dialog
      open={!!shareNote}
      onOpenChange={(open) => {
        if (!open) {
          setShareNote(null);
          setShareError(null);
        }
      }}
    >
    <DialogContent className="max-w-[min(680px,92vw)] p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
      <ModalPanel className="p-6">

        {shareNote ? (
          <div className="space-y-4">

            {/* Header */}
            <div className="flex items-start justify-between pb-3 border-b border-purple-500/10">
              <div className="space-y-1">
                <div className="text-lg font-semibold text-foreground">
                  Manage shares
                </div>

                <div className="text-sm text-muted-foreground">
                  Manage who has access to this note and their permissions.
                </div>

                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Note:</span>{" "}
                  {shareNote.title || "Untitled"}
                </div>
              </div>

              <CloseButton onClick={() => setShareNote(null)} />
            </div>

            {shareError ? (
              <div className="text-xs text-destructive">{shareError}</div>
            ) : null}

            {/* Recipient + Permission */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">

              <div className="sm:col-span-2">
                <label className="text-[11px] text-muted-foreground">
                  Recipient
                </label>

                <select
                  className="w-full text-sm border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                  value={shareRecipient}
                  onChange={(e) => setShareRecipient(e.target.value)}
                  disabled={
                    shareLoading || !!shareError || shareMembers.length === 0
                  }
                >
                  <option value="">
                    {shareLoading
                      ? "Loading…"
                      : shareMembers.length
                      ? "Select a user…"
                      : "No eligible members"}
                  </option>

                  {shareMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground">
                  Permission
                </label>

                <select
                  className="w-full text-sm border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                  value={sharePermission}
                  onChange={(e) =>
                    setSharePermission(e.target.value as SharePermission)
                  }
                  disabled={shareLoading || !!shareError}
                >
                  <option value="read">Read</option>
                  <option value="write">Read / Write</option>
                </select>
              </div>

            </div>

            {/* Toggle + Share */}
            <div className="flex items-center justify-between gap-2">

              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={shareIncludeRevoked}
                  onChange={(e) => setShareIncludeRevoked(e.target.checked)}
                  className="h-3 w-3"
                />
                Show revoked
              </label>

              <Button
                onClick={submitShare}
                disabled={shareLoading || !shareRecipient || !!shareError}
              >
                Share
              </Button>

            </div>

            {/* Existing shares */}
            <div className="rounded-md border border-border bg-window/60 p-2">
              <div className="text-xs font-semibold mb-2">
                Existing shares
              </div>

              {!shareList ? (
                <div className="text-xs text-muted-foreground">
                  {shareLoading ? "Loading…" : "No data"}
                </div>
              ) : shareList.shares.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  No shares yet.
                </div>
              ) : (
                <div className="space-y-1">

                  {shareList.shares.map((s) => {
                    const profile =
                      shareList.profiles.find(
                        (p) => p.user_id === s.principal_user_id
                      ) || null;

                    const recipientLabel =
                      (profile?.display_name || "").trim() ||
                      (profile?.email || "").trim() ||
                      shortUserId(s.principal_user_id);

                    return (
                      <div
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-xs"
                      >
                        <div className="min-w-0 truncate">
                          {recipientLabel}
                          <span className="text-muted-foreground">
                            {" "}
                            • {s.permission}
                            {s.revoked_at ? " • revoked" : ""}
                          </span>
                        </div>

                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRevokeShare(s.id)}
                          disabled={shareLoading || !!s.revoked_at}
                        >
                          Revoke
                        </Button>
                      </div>
                    );
                  })}

                </div>
              )}
            </div>

          </div>
        ) : null}

      </ModalPanel>
    </DialogContent>
    </Dialog>

    <AlertDialog open={!!confirmDeleteNote} onOpenChange={(open) => { if (!open) setConfirmDeleteNote(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete note</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this note? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { if (confirmDeleteNote) executeDeleteNote(confirmDeleteNote); }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
  );
}